import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  ANSWER_REMARK_PLUGINS,
  drawnPassageIdOf,
  holdsDrawnPassage,
  lineRangeOf,
  PASSAGE_RENDER_TAGS,
  passageIdOf,
  passageLinesOf,
  PositionedNode,
} from './answerPassageElements';
import { AnswerPassageIndex } from './answerPassages.utils';
import { BP_ANSWER_PROSE_CLASSES, BP_ANSWER_SURFACE_CLASSES } from './brandPresenceClasses';
import { CellPaintByLine, CellPaint, LinePaint } from './GovernanceAiAnswerDetail.utils';
import { withAlpha } from './governanceThemeColors';

/**
 * The answer reading surface: the model's markdown, with per-unit brand tints and per-name
 * highlights on top.
 *
 * Kept in its own module with no dependency on the detail page's data plumbing, so the rendered
 * output can be asserted directly — the table has to survive painting, and that is only provable
 * against the real markdown renderer.
 */

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface BrandMatcher {
  name: string;
  color: string;
  /** The colour for the highlighted NAME text: a stored colour darkened for readability, else `color`. */
  textColor?: string;
  ownership: 'own' | 'competitor' | 'other';
}

/** How strongly a brand's name is marked in the text: a soft marker, never a solid block. */
const BRAND_MARKER_ALPHA = 0.14;

/** How strongly a passage is tinted with the colour of the brand it is about. */
const BRAND_PASSAGE_ALPHA = 0.1;

/** Wraps occurrences of tracked brand names in a text string with soft, rounded markers. */
function highlightText(text: string, matchers: BrandMatcher[], keyPrefix: string): React.ReactNode {
  if (!matchers.length || !text) {
    return text;
  }

  const pattern = new RegExp(`(${matchers.map((matcher) => escapeRegExp(matcher.name)).join('|')})`, 'gi');
  const segments = text.split(pattern);

  return segments.map((segment, index) => {
    const matcher = matchers.find((entry) => entry.name.toLowerCase() === segment.toLowerCase());
    if (!matcher) {
      return <React.Fragment key={`${keyPrefix}-${index}`}>{segment}</React.Fragment>;
    }

    return (
      <span
        key={`${keyPrefix}-${index}`}
        // No vertical padding (the line height must not shift), rounded ends that a name wrapping
        // across two lines keeps on both of them, and a hair of horizontal padding.
        className="rounded-[0.3rem] px-0.5 font-semibold box-decoration-clone"
        // The marker is the brand's own colour, known only at runtime: the tint is the raw colour, the
        // name text the readable variant when one was derived.
        style={{ backgroundColor: withAlpha(matcher.color, BRAND_MARKER_ALPHA), color: matcher.textColor ?? matcher.color }}
      >
        {segment}
      </span>
    );
  });
}

/** Recursively applies brand highlighting to ReactMarkdown children. */
function highlightChildren(children: React.ReactNode, matchers: BrandMatcher[], keyPrefix: string): React.ReactNode {
  if (typeof children === 'string') {
    return highlightText(children, matchers, keyPrefix);
  }
  if (Array.isArray(children)) {
    return children.map((child, index) => (
      <React.Fragment key={`${keyPrefix}-${index}`}>{highlightChildren(child, matchers, `${keyPrefix}-${index}`)}</React.Fragment>
    ));
  }
  return children;
}

/**
 * The passages of the answer and what the reader is doing with them. Present once the answer has
 * been analysed; without it every passage renders as plain, tinted text.
 */
export interface PassageInteraction {
  passages: AnswerPassageIndex;
  /** The passage under the pointer or the keyboard focus, whose card is showing. */
  activeId?: string;
  /** The passage the reader clicked to keep its card open. */
  pinnedId?: string;
  onHover: (unitId: string | undefined) => void;
  /** Pins the passage, or releases it when it is the one pinned. */
  onToggle: (unitId: string) => void;
  onUnpin: () => void;
}

/** The colour that marks the pinned passage: its accent bar and its hairline. */
const PINNED_PASSAGE_COLOR = '#0284c7';

/** The colour of the hairline around the pinned passage, and around the one whose card is showing. */
const PINNED_PASSAGE_OUTLINE_COLOR = withAlpha(PINNED_PASSAGE_COLOR, 0.35);
const ACTIVE_PASSAGE_OUTLINE_COLOR = '#cbd5e1';

/** The tint of the pinned passage, and of the one whose card is showing, where no colour shades it. */
const PINNED_PASSAGE_TINT = withAlpha(PINNED_PASSAGE_COLOR, 0.06);
const ACTIVE_PASSAGE_TINT = 'rgba(148, 163, 184, 0.1)';

/** The width of the bar along a passage's leading edge: its grade's colour, or the pin's. */
const ACCENT_BAR_WIDTH = '3px';

/** The fine dotted underline of a passage that grades its brands differently. */
const GRADED_DIFFERENTLY_UNDERLINE_COLOR = '#94a3b8';

/**
 * The spacing every block passage takes, painted or not, so pointing at a passage, pinning it or
 * switching the colouring never moves the text: the tint reaches a little past the text on either
 * side, and the text stays where the rest of the answer's text is.
 */
const PASSAGE_BLOCK_CLASSES = 'rounded-md -mx-2 px-2 py-0.5 transition-[background-color,box-shadow,outline-color] duration-150';

/** A bar along the leading edge of an element, drawn inside it so nothing moves. */
function accentBar(color: string): string {
  return `inset ${ACCENT_BAR_WIDTH} 0 0 ${color}`;
}

/** Whether a click landed on something interactive of its own — a link — which must keep its own behaviour. */
function isOwnInteractiveTarget(target: EventTarget | null, passageElement: Element): boolean {
  const interactive = target instanceof Element ? target.closest('a, button, input, select, textarea, summary') : null;
  return !!interactive && interactive !== passageElement && passageElement.contains(interactive);
}

/** Moves keyboard focus to the passage before or after the one focused, in reading order. */
function focusNeighbourPassage(current: HTMLElement, step: 1 | -1): void {
  const container = current.closest('[data-answer-passages]');
  if (!container) {
    return;
  }
  const passages = Array.from(container.querySelectorAll<HTMLElement>('[data-passage-id]'));
  const next = passages[passages.indexOf(current) + step];
  next?.focus();
}

/** Everything the renderers paint with and react to, handed down by `HighlightedAnswer`. */
interface AnswerRendering {
  matchers: BrandMatcher[];
  paintByLine: (LinePaint | undefined)[];
  nameByTermKey: Map<string, string>;
  ownTermKeys: ReadonlySet<string>;
  spotlightLines?: Set<number>;
  cellPaintByLine?: CellPaintByLine;
  spotlightCells?: Map<number, Set<number>>;
  interaction?: PassageInteraction;
}

/**
 * The renderers read the paint and the reader's hover and pin from here rather than from a closure,
 * so they are defined once and keep their identity: pointing at or pinning a passage re-renders the
 * answer in place and never mounts it afresh. A fresh mount would replace the element under the
 * pointer between the press and the release of a click (the browser then fires no click, and the pin
 * is lost) and would take the keyboard focus with it.
 */
const AnswerRenderingContext = React.createContext<AnswerRendering>({
  matchers: [],
  paintByLine: [],
  nameByTermKey: new Map(),
  ownTermKeys: new Set(),
});

/** The key prefix of the brand-name highlights inside the answer. */
const HIGHLIGHT_KEY_PREFIX = 'answer';

type RendererProps = { children?: React.ReactNode; node?: PositionedNode };

/** What makes a rendered element a passage the reader can point at, pin and step through. */
function passageProps(interaction: PassageInteraction | undefined, unitId: string | undefined): Record<string, unknown> {
  if (!interaction || !unitId) {
    return {};
  }
  // The one passage the Tab key lands on: the pinned one, else the first. Arrows move from there.
  const tabStopId = interaction.pinnedId ?? interaction.passages.order[0];
  return {
    'data-passage-id': unitId,
    tabIndex: unitId === tabStopId ? 0 : -1,
    // Every passage keeps its own semantics — a heading stays a heading to a screen reader, a list
    // item stays in its list — and the pinned one is marked as the current passage.
    ...(interaction.pinnedId === unitId ? { 'aria-current': 'true' } : {}),
    onMouseEnter: () => interaction.onHover(unitId),
    onMouseLeave: () => interaction.onHover(undefined),
    // The passage's own focus only. A link inside it taking the focus leaves the card as it is, so a
    // pinned passage's card stays reachable by Tab past the links that come before it.
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) {
        interaction.onHover(unitId);
      }
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) {
        interaction.onHover(undefined);
      }
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      if (isOwnInteractiveTarget(event.target, event.currentTarget)) {
        return;
      }
      event.stopPropagation();
      interaction.onToggle(unitId);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        interaction.onToggle(unitId);
      } else if (event.key === 'Escape') {
        interaction.onUnpin();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        focusNeighbourPassage(event.currentTarget, event.key === 'ArrowDown' ? 1 : -1);
      }
    },
  };
}

/**
 * The passage whose card is showing and the pinned one: a hairline around each, a light tint where
 * no colour shades the passage already, and on the pinned one a bar along its leading edge. A table
 * row keeps only the hairline — its first cell carries the bar. `strength` below 1 draws all of it
 * fainter, as the passage's own dimmed text is.
 */
function passageStateStyle({
  interaction,
  unitId,
  paint,
  block,
  strength,
}: {
  interaction: PassageInteraction | undefined;
  unitId: string | undefined;
  paint: LinePaint | undefined;
  block: boolean;
  strength: number;
}): React.CSSProperties {
  if (!interaction || !unitId) {
    return {};
  }
  const pinned = interaction.pinnedId === unitId;
  const active = !pinned && interaction.activeId === unitId;
  if (!pinned && !active) {
    return { cursor: 'pointer' };
  }
  const shaded = !!paint && (block || paint.accent);
  const at = (color: string) => (strength < 1 ? fadedColor(color, strength) : color);
  return {
    cursor: 'pointer',
    outline: `1px solid ${at(pinned ? PINNED_PASSAGE_OUTLINE_COLOR : ACTIVE_PASSAGE_OUTLINE_COLOR)}`,
    outlineOffset: '0',
    ...(shaded ? {} : { backgroundColor: at(pinned ? PINNED_PASSAGE_TINT : ACTIVE_PASSAGE_TINT) }),
    ...(pinned && block ? { boxShadow: accentBar(at(PINNED_PASSAGE_COLOR)) } : {}),
  };
}

/**
 * How strongly the spotlight shows what it does not light: the opacity of a dimmed element, and the
 * share of its strength a container's own paint keeps while only an item inside it is lit.
 */
const SPOTLIGHT_DIMMED_OPACITY = 0.5;

/** How the spotlight's changes fade in and out, on every element it lights or dims. */
const SPOTLIGHT_TRANSITION: React.CSSProperties = { transition: 'opacity 150ms ease-out' };

/** How the spotlight dims what it does not light. Opacity only, so spotlighting never reflows the answer. */
const SPOTLIGHT_DIMMED_STYLE: React.CSSProperties = { ...SPOTLIGHT_TRANSITION, opacity: SPOTLIGHT_DIMMED_OPACITY };
const SPOTLIGHT_LIT_STYLE: React.CSSProperties = { ...SPOTLIGHT_TRANSITION, opacity: 1 };

/**
 * What the element around decides for the elements inside it, under a spotlight.
 *
 * - `free`: nothing; each decides by its own lines.
 * - `dimmed`: the element around is dimmed already, so nothing inside it dims again.
 * - `lit`: the passage around is lit; what is part of it is lit with it, while a passage drawn inside
 *   it still decides for itself.
 */
type SpotlightScope = 'free' | 'dimmed' | 'lit';

const SpotlightScopeContext = React.createContext<SpotlightScope>('free');

/** Whether the spotlight lights any of these lines. A row with per-cell spotlighting stays lit; its cells decide individually. */
function spotlightLitAt(rendering: AnswerRendering, lines: Iterable<number>): boolean {
  for (const line of lines) {
    if (rendering.spotlightLines?.has(line) || rendering.spotlightCells?.has(line)) {
      return true;
    }
  }
  return false;
}

/** How the spotlight treats one drawn element, and what it decides for the elements inside it. */
interface SpotlightTreatment {
  /** The element's own opacity, when it takes one. */
  style: React.CSSProperties;
  /** Whether what the element holds itself is dimmed where it stands, around the passages drawn inside it. */
  dimOwn: boolean;
  /** What it decides for the elements inside it. */
  inside: SpotlightScope;
}

/**
 * How the spotlight lights an element: by the lines it holds itself — the passages drawn inside it
 * are lit or dimmed by their own lines, so a list item around a lit nested item is not lit by it, and
 * the page's attribute verdicts read the same lines (`passageLinesOf`).
 *
 * An element with a passage drawn inside it takes no opacity while anything inside it is lit, since
 * a dimmed element would dim the lit passage too; if its own lines are not lit, what it holds itself
 * is dimmed where it stands instead, its paint included. With nothing inside it lit, it dims whole.
 * What is part of a passage — a list item's paragraph, a quoted line drawn within a longer quote, an
 * item with no card of its own — lights and dims with the passage.
 */
function spotlightTreatment({
  rendering,
  node,
  isPassage,
  scope,
}: {
  rendering: AnswerRendering;
  node: PositionedNode | undefined;
  isPassage: boolean;
  scope: SpotlightScope;
}): SpotlightTreatment {
  const range = lineRangeOf(node);
  if (!rendering.spotlightLines || !node || !range || scope === 'dimmed') {
    return { style: {}, dimOwn: false, inside: scope };
  }
  if (!isPassage && scope === 'lit') {
    return { style: {}, dimOwn: false, inside: 'lit' };
  }
  const passages = rendering.interaction?.passages;
  const ownLit = spotlightLitAt(rendering, passageLinesOf(passages, node));
  if (!holdsDrawnPassage(passages, node)) {
    return ownLit
      ? { style: SPOTLIGHT_LIT_STYLE, dimOwn: false, inside: 'lit' }
      : { style: SPOTLIGHT_DIMMED_STYLE, dimOwn: false, inside: 'dimmed' };
  }
  if (!spotlightLitAt(rendering, rangeLines(range))) {
    return { style: SPOTLIGHT_DIMMED_STYLE, dimOwn: false, inside: 'dimmed' };
  }
  // Lit or dimming only its own content, the element keeps full opacity, fading in and out like the rest.
  return ownLit
    ? { style: SPOTLIGHT_TRANSITION, dimOwn: false, inside: 'lit' }
    : { style: SPOTLIGHT_TRANSITION, dimOwn: true, inside: 'free' };
}

/** Every line of a range. */
function rangeLines(range: { start: number; end: number }): number[] {
  const lines: number[] = [];
  for (let line = range.start; line <= range.end; line += 1) {
    lines.push(line);
  }
  return lines;
}

/** Whether a child of an element is, or holds, a passage drawn on its own — which the element leaves to light itself. */
function holdsOwnPassage(passages: AnswerPassageIndex | undefined, node: PositionedNode | undefined): boolean {
  return !!node && node.type === 'element' && (!!drawnPassageIdOf(passages, node) || holdsDrawnPassage(passages, node));
}

/**
 * One child of a container, dimmed where it stands: text in a span, an element by its style. What the
 * element holds reads the child as dimmed already, so nothing inside it — the items of a list with
 * no cards — dims a second time.
 */
function dimmedChild(child: React.ReactNode, content: React.ReactNode): React.ReactNode {
  if (typeof child === 'string' || typeof child === 'number') {
    return <span style={SPOTLIGHT_DIMMED_STYLE}>{content}</span>;
  }
  return React.isValidElement<{ style?: React.CSSProperties }>(child)
    ? React.cloneElement(child, { style: { ...child.props.style, ...SPOTLIGHT_DIMMED_STYLE } })
    : content;
}

/**
 * An element's children, brand names highlighted, and — where it dims what it holds itself — each
 * child that is not, and holds no, passage of its own dimmed where it stands. Every child element
 * sits in the same place, under the same keys, whatever the spotlight does — only what it is told
 * changes — so nothing inside mounts afresh when the spotlight moves: a passage or a link keeps the
 * pointer and the keyboard focus, and a dimmed child fades rather than appearing dimmed.
 */
function elementContent({
  children,
  node,
  rendering,
  keyPrefix,
  dimOwn,
  inside,
}: {
  children: React.ReactNode;
  node: PositionedNode | undefined;
  rendering: AnswerRendering;
  keyPrefix: string;
  dimOwn: boolean;
  inside: SpotlightScope;
}): React.ReactNode {
  const passages = rendering.interaction?.passages;
  const nodeChildren = node?.children ?? [];
  const list = Array.isArray(children) ? children : [children];
  // The renderer hands a list item's or a quote's children over one for one, in the parse's order;
  // should they ever differ, nothing is dimmed rather than the wrong child.
  const aligned = list.length === nodeChildren.length;
  const dimsChild = (index: number): boolean => dimOwn && aligned && !holdsOwnPassage(passages, nodeChildren[index]);
  const one = (child: React.ReactNode, index: number, prefix: string) => {
    const content = highlightChildren(child, rendering.matchers, prefix);
    const dims = dimsChild(index);
    const shown = dims ? dimmedChild(child, content) : content;
    // A dimmed child's own content reads it as dimmed already, so none of it dims a second time.
    return React.isValidElement(child) ? (
      <SpotlightScopeContext.Provider value={dims ? 'dimmed' : inside}>{shown}</SpotlightScopeContext.Provider>
    ) : (
      shown
    );
  };
  if (Array.isArray(children)) {
    return children.map((child, index) => (
      <React.Fragment key={`${keyPrefix}-${index}`}>{one(child, index, `${keyPrefix}-${index}`)}</React.Fragment>
    ));
  }
  return one(children, 0, keyPrefix);
}

/**
 * The fine dotted underline of a passage the verdict lens shades for one brand while it grades
 * another differently. A table row underlines its text instead: in a collapsed table the cells' own
 * borders win over a border on the row, so a border there would never show.
 */
function gradedDifferentlyStyle(tag: keyof JSX.IntrinsicElements, strength: number): React.CSSProperties {
  const color = strength < 1 ? fadedColor(GRADED_DIFFERENTLY_UNDERLINE_COLOR, strength) : GRADED_DIFFERENTLY_UNDERLINE_COLOR;
  return tag === 'tr' ? { textDecoration: `underline dotted ${color}` } : { borderBottom: `1px dotted ${color}` };
}

/**
 * A colour at a share of its own strength: its alpha — from `rgba()`, or the last two digits of a
 * `#rrggbbaa` hex, as the verdict tints are written — or full opacity for a colour without one,
 * times the share.
 */
function fadedColor(color: string, strength: number): string {
  const value = color.trim();
  const hexWithAlpha = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(value);
  if (hexWithAlpha) {
    return withAlpha(`#${hexWithAlpha[1]}`, (Number.parseInt(hexWithAlpha[2], 16) / 255) * strength);
  }
  const alpha = /^rgba\([^)]*,\s*([\d.]+)\s*\)$/i.exec(value)?.[1];
  return withAlpha(value, (alpha === undefined ? 1 : Number(alpha)) * strength);
}

/** The share of a passage's colour the dashed line of a passage shared between brands is drawn at. */
const SHARED_PASSAGE_LINE_STRENGTH = 0.5;

/**
 * How a paint shades an element. A block passage takes a light tint of its colour and, in the
 * verdict colouring, a bar in the full colour along its leading edge. A table row is shaded only in
 * the verdict colouring; a brand's row carries a slim stripe on its first cell instead of a fill.
 * `strength` below 1 draws all of it fainter, as an element's own dimmed text is.
 */
function paintStyle(tag: keyof JSX.IntrinsicElements, paint: LinePaint, strength: number): React.CSSProperties {
  const block = tag !== 'tr';
  const tint = paint.backgroundColor ?? withAlpha(paint.color, BRAND_PASSAGE_ALPHA);
  return {
    ...(block || paint.accent ? { backgroundColor: strength < 1 ? fadedColor(tint, strength) : tint } : {}),
    ...(block && paint.accent ? { boxShadow: accentBar(strength < 1 ? fadedColor(paint.color, strength) : paint.color) } : {}),
    ...(paint.mixed ? { borderBottom: `1px dashed ${fadedColor(paint.color, SHARED_PASSAGE_LINE_STRENGTH * strength)}` } : {}),
    ...(paint.gradedDifferently ? gradedDifferentlyStyle(tag, strength) : {}),
  };
}

/**
 * Tints a rendered element in place. Nothing is injected into the markdown source — the source
 * is parsed once, whole, so table syntax (and every other block construct) survives painting.
 */
function tinted(tag: keyof JSX.IntrinsicElements, options?: { pad?: boolean }) {
  const block = options?.pad !== false;
  return function TintedRenderer({
    children,
    node,
    style: givenStyle,
    ...props
  }: RendererProps & { className?: string; style?: React.CSSProperties }) {
    const rendering = React.useContext(AnswerRenderingContext);
    const scope = React.useContext(SpotlightScopeContext);
    const { interaction } = rendering;
    const range = lineRangeOf(node);
    const paint = range ? rendering.paintByLine[range.start] : undefined;
    const passageId = passageIdOf(interaction?.passages, tag, node);
    const spotlight = spotlightTreatment({ rendering, node, isPassage: !!passageId, scope });
    // Runtime values throughout: the colours are the brand's or the grade's, and the state is the
    // reader's pointer and pin. The pinned passage's bar comes last, so it replaces a grade's bar. A
    // style handed down by a container that dims this element where it stands comes first.
    // What an element dims where it stands, its paint and its pointer and pin state included, keeps the
    // same share of its strength as its dimmed text.
    const strength = spotlight.dimOwn ? SPOTLIGHT_DIMMED_OPACITY : 1;
    const style: React.CSSProperties = {
      ...givenStyle,
      ...spotlight.style,
      ...(paint ? paintStyle(tag, paint, strength) : {}),
      ...passageStateStyle({ interaction, unitId: passageId, paint, block, strength }),
    };
    const className = [props.className, block && (paint || passageId) ? PASSAGE_BLOCK_CLASSES : undefined].filter(Boolean).join(' ');

    return React.createElement(
      tag,
      {
        ...props,
        ...(className ? { className } : {}),
        ...(Object.keys(style).length ? { style } : {}),
        // Where passages are point-able, the passage card says who a passage is about, and more.
        ...(paint && !interaction ? { title: describePassage(paint, rendering.nameByTermKey, rendering.ownTermKeys) } : {}),
        ...passageProps(interaction, passageId),
      },
      elementContent({
        children,
        node,
        rendering,
        keyPrefix: `${HIGHLIGHT_KEY_PREFIX}-${tag}`,
        dimOwn: spotlight.dimOwn,
        inside: spotlight.inside,
      })
    );
  };
}

/** Inline elements carry no tint of their own — they inherit their block's. */
function inline(tag: keyof JSX.IntrinsicElements) {
  return function InlineRenderer({ children, node, ...props }: RendererProps) {
    const { matchers } = React.useContext(AnswerRenderingContext);
    return React.createElement(tag, props, highlightChildren(children, matchers, `${HIGHLIGHT_KEY_PREFIX}-${tag}`));
  };
}

/**
 * A table cell. In a column-keyed comparison table each column belongs to a different brand, so
 * the tint lives here rather than on the row. `columnIndex` is injected by the row renderer,
 * which is the only place the grid order is known.
 */
function cell(tag: 'td' | 'th') {
  return function CellRenderer({ children, node, columnIndex, ...props }: RendererProps & { columnIndex?: number }) {
    const { matchers, paintByLine, cellPaintByLine, spotlightCells, nameByTermKey, interaction } = React.useContext(AnswerRenderingContext);
    const insideDimmed = React.useContext(SpotlightScopeContext) === 'dimmed';
    const range = lineRangeOf(node);
    const paint = range && typeof columnIndex === 'number' ? cellPaintByLine?.get(range.start)?.get(columnIndex) : undefined;
    // The row's own paint, drawn as a slim stripe on its first cell rather than a fill across the row.
    const rowPaint = range && columnIndex === 0 && !paint ? paintByLine[range.start] : undefined;

    const columns = range ? spotlightCells?.get(range.start) : undefined;
    // Inside an element the spotlight dims whole, the cell is dimmed with it already.
    const dimmed = columns && typeof columnIndex === 'number' && !columns.has(columnIndex) && !insideDimmed;

    // Runtime values: the colours are the brand's or the grade's.
    const style: React.CSSProperties = {
      ...(dimmed ? SPOTLIGHT_DIMMED_STYLE : {}),
      ...(paint ? cellPaintStyle(paint) : {}),
      ...(rowPaint ? { boxShadow: accentBar(rowPaint.color) } : {}),
    };

    return React.createElement(
      tag,
      {
        ...props,
        ...(Object.keys(style).length ? { style } : {}),
        ...(paint && !interaction ? { title: nameByTermKey.get(paint.entityKey) } : {}),
      },
      highlightChildren(children, matchers, `${HIGHLIGHT_KEY_PREFIX}-${tag}`)
    );
  };
}

/** A cell of a table comparing brands side by side: a light tint of its brand, and its grade's bar. */
function cellPaintStyle(paint: CellPaint): React.CSSProperties {
  return {
    backgroundColor: paint.backgroundColor ?? withAlpha(paint.color, BRAND_PASSAGE_ALPHA),
    ...(paint.accent ? { boxShadow: accentBar(paint.color) } : {}),
  };
}

const TintedRow = tinted(PASSAGE_RENDER_TAGS.tr, { pad: false });

/**
 * The row renderer hands each cell its grid position. React only knows the order here, and the
 * backend's `column` index is defined against exactly this order.
 */
function RowRenderer(props: RendererProps) {
  let columnIndex = 0;
  const children = React.Children.map(props.children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<{ columnIndex?: number }>, { columnIndex: columnIndex++ })
      : child
  );
  return <TintedRow {...props}>{children}</TintedRow>;
}

/**
 * A wide comparison table scrolls inside the answer box instead of pushing past it. A table
 * with several long columns is the normal shape of a comparison answer, and without this the
 * content ran outside its container and took the page's horizontal scrollbar with it.
 *
 * The wrapper is purely a scroll container — it carries no tinting and does not touch the
 * table's own children, so the per-cell column indexing the painter relies on is unaffected.
 */
function TableRenderer({ children, node, ...props }: RendererProps) {
  return (
    <div className="overflow-x-auto max-w-full">
      <table {...props}>{children}</table>
    </div>
  );
}

/** Long unbroken code lines overflow for the same reason; contain them the same way. */
function PreRenderer({ children, node, ...props }: RendererProps) {
  return (
    <pre {...props} className="overflow-x-auto max-w-full">
      {children}
    </pre>
  );
}

function LinkRenderer({ children, node, ...props }: RendererProps) {
  const { matchers } = React.useContext(AnswerRenderingContext);
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {highlightChildren(children, matchers, `${HIGHLIGHT_KEY_PREFIX}-a`)}
    </a>
  );
}

/** One set of renderers for every answer, created once. */
const ANSWER_COMPONENTS = {
  // The elements drawn as passages, each as the tag the passage rules name for it.
  p: tinted(PASSAGE_RENDER_TAGS.p),
  li: tinted(PASSAGE_RENDER_TAGS.li),
  h1: tinted(PASSAGE_RENDER_TAGS.h1),
  h2: tinted(PASSAGE_RENDER_TAGS.h2),
  h3: tinted(PASSAGE_RENDER_TAGS.h3),
  blockquote: tinted(PASSAGE_RENDER_TAGS.blockquote),
  table: TableRenderer,
  pre: PreRenderer,
  // A table row is tinted only when its cells are not; padding would break the cell grid.
  tr: RowRenderer,
  strong: inline('strong'),
  em: inline('em'),
  td: cell('td'),
  th: cell('th'),
  a: LinkRenderer,
};

/**
 * The hover description of a tinted passage: whose it is, and — when the credit was split — what
 * share of the passage they hold.
 */
export function describePassage(
  paint: LinePaint,
  nameByTermKey: Map<string, string>,
  ownTermKeys: ReadonlySet<string>
): string | undefined {
  const name = nameByTermKey.get(paint.entityKey) || 'This brand';
  const share = typeof paint.share === 'number' && Number.isFinite(paint.share) ? Math.round(paint.share * 100) : undefined;

  if (share !== undefined) {
    return `${name} — ${share}% of this passage${paint.mixed ? ' (shared with other brands)' : ''}`;
  }

  if (paint.mixed) {
    return `${name} — this passage also covers other brands`;
  }

  // "About them" is right for a rival and wrong for the reader's own brand, which the sidebar and
  // this tooltip both label "You" — "You — this passage is about them" reads as a third party.
  // Decided from the term's OWNERSHIP, resolved upstream through `classifyEntityOwnership`, rather
  // than by recognising the word "You": the label is a display string and can change, and matching
  // on it would also mis-read any rival a customer happened to name that way.
  return ownTermKeys.has(paint.entityKey) ? `${name} — this passage is about you` : `${name} — this passage is about them`;
}

/**
 * Renders the answer with per-unit soft backgrounds (so a passage discussed for three
 * paragraphs is literally shown as three tinted paragraphs) plus the hard per-name
 * highlights on top.
 */
export function HighlightedAnswer({
  markdown,
  paintByLine,
  cellPaintByLine,
  matchers,
  nameByTermKey,
  ownTermKeys,
  spotlightLines,
  spotlightCells,
  interaction,
}: {
  /** The answer's markdown, parsed once and whole — never sliced for painting. */
  markdown: string;
  /** Tint per source line; the renderer applies it to the elements those lines produced. */
  paintByLine: (LinePaint | undefined)[];
  /** Tint per table cell, for column-keyed comparison tables. Absent until the data carries it. */
  cellPaintByLine?: CellPaintByLine;
  matchers: BrandMatcher[];
  /** Term key -> display name, so a tinted passage can say whose it is on hover. */
  nameByTermKey: Map<string, string>;
  /**
   * The terms that are the customer's OWN, from the page's shared ownership classification.
   *
   * Carried so a passage tooltip can address the reader in the second person rather than calling
   * their own brand "them".
   */
  ownTermKeys: ReadonlySet<string>;
  /**
   * When set, the drawn elements holding one of these lines themselves stay at full opacity and the
   * others dim; headings below `###` and code blocks, drawn as plain text, take no part.
   */
  spotlightLines?: Set<number>;
  /** Rows spotlighted per cell: line index -> the columns that belong to the hovered subject. */
  spotlightCells?: Map<number, Set<number>>;
  /** Makes each analysed passage point-able: hover previews its card, a click pins it. */
  interaction?: PassageInteraction;
}) {
  const rendering = useMemo<AnswerRendering>(
    () => ({ matchers, paintByLine, nameByTermKey, ownTermKeys, spotlightLines, cellPaintByLine, spotlightCells, interaction }),
    [matchers, paintByLine, nameByTermKey, ownTermKeys, spotlightLines, cellPaintByLine, spotlightCells, interaction]
  );
  // Parsed once per answer: a new hover or pin reaches the renderers through the context alone.
  const tree = useMemo(
    () => (
      <ReactMarkdown remarkPlugins={ANSWER_REMARK_PLUGINS} components={ANSWER_COMPONENTS}>
        {markdown}
      </ReactMarkdown>
    ),
    [markdown]
  );

  return (
    <div data-answer-passages className={`${BP_ANSWER_SURFACE_CLASSES} overflow-x-hidden ${BP_ANSWER_PROSE_CLASSES}`}>
      <AnswerRenderingContext.Provider value={rendering}>{tree}</AnswerRenderingContext.Provider>
    </div>
  );
}
